import { useStableCallback } from "@prismicio/editor-support/React"
import { filter, slice } from "@prismicio/editor-support/Set"
import {
  type KeyboardEvent,
  type RefObject,
  useLayoutEffect,
  useState,
} from "react"

const gapBetweenTags = 8
const maxInputContainerSize = 225

interface UseTagFieldHeaderProps {
  selectedValues: Set<string>
  tagsContainerRef: RefObject<HTMLDivElement>
  open: boolean
  onSelectedValuesChange: (selectedValues: Set<string>) => void
}

export function useTagFieldHeader(props: UseTagFieldHeaderProps) {
  const { selectedValues, tagsContainerRef, open, onSelectedValuesChange } =
    props

  const [closeDisplayedTags, setClosedDisplayedTags] = useState(selectedValues)

  // TODO: This is a temporary solution to make real-time syncing of tags possible,
  // as part of real-time collaboration feature.
  // https://linear.app/prismic/issue/PBD-2163/editor-rewrite-usetagfieldheader
  function measureTags(container: HTMLDivElement) {
    const containerWidth = container.clientWidth
    const maxTagsContainerWidth = containerWidth - maxInputContainerSize

    // Get styles from an existing tag if available
    const existingTag = container.querySelector('[data-marker="badge"]')
    const tagStyles = existingTag ? window.getComputedStyle(existingTag) : null

    // Create a temporary container to measure tags
    const tempContainer = document.createElement("div")
    tempContainer.style.visibility = "hidden"
    tempContainer.style.position = "absolute"
    tempContainer.style.display = "flex"
    tempContainer.style.gap = `${gapBetweenTags}px`
    document.body.appendChild(tempContainer)

    // Create and measure each tag
    const tagElements = Array.from(selectedValues).map((value) => {
      const tag = document.createElement("div")
      tag.textContent = value
      tag.dataset["marker"] = "badge"

      // Copy styles from existing tag
      if (tagStyles) {
        tag.style.padding = tagStyles.padding
        tag.style.margin = tagStyles.margin
        tag.style.fontSize = tagStyles.fontSize
        tag.style.fontFamily = tagStyles.fontFamily
        tag.style.lineHeight = tagStyles.lineHeight
        tag.style.borderRadius = tagStyles.borderRadius
        tag.style.border = tagStyles.border
      }

      tempContainer.appendChild(tag)
      return tag
    })

    const { displayedTags } = calculateDisplayedTags({
      tagList: tagElements,
      maxTagsContainerWidth,
    })

    // Clean up
    document.body.removeChild(tempContainer)

    return displayedTags
  }

  const updateDisplayedTagsOnClose = useStableCallback(
    (container: HTMLDivElement) => {
      const displayedTags = measureTags(container)
      setClosedDisplayedTags(new Set(displayedTags))
    },
  )

  useLayoutEffect(() => {
    if (tagsContainerRef.current) {
      updateDisplayedTagsOnClose(tagsContainerRef.current)
    }
  }, [selectedValues, updateDisplayedTagsOnClose, tagsContainerRef])

  const extraTags = selectedValues.size - closeDisplayedTags.size

  const displayedSelectedValues = open ? selectedValues : closeDisplayedTags

  function removeSelectedValue(valueToDelete: string | LastItemLocation) {
    if (valueToDelete === -1) {
      const updatedValues = slice(selectedValues, 0, selectedValues.size - 1)
      return onSelectedValuesChange(updatedValues)
    }

    const filteredValues = filter(
      selectedValues,
      (value) => value !== valueToDelete,
    )
    return onSelectedValuesChange(filteredValues)
  }

  return {
    removeSelectedValue,
    displayedSelectedValues,
    extraTags,
  }
}

export type LastItemLocation = -1

interface calculateDisplayedTags {
  tagList: Element[]
  maxTagsContainerWidth: number
}
function calculateDisplayedTags(props: calculateDisplayedTags) {
  const { tagList, maxTagsContainerWidth } = props

  return tagList.reduce<{
    displayedTags: string[]
    tagsContainerWidth: number
  }>(
    ({ displayedTags, tagsContainerWidth }, tag) => {
      if (!(tag instanceof HTMLDivElement) || !tag.textContent)
        return { displayedTags, tagsContainerWidth }

      const currentContainerWidth =
        tagsContainerWidth + tag.offsetWidth + gapBetweenTags

      if (
        tag.dataset["marker"] === "badge" &&
        currentContainerWidth <= maxTagsContainerWidth
      )
        return {
          displayedTags: [...displayedTags, tag.textContent?.trim()],
          tagsContainerWidth: currentContainerWidth,
        }

      return { displayedTags, tagsContainerWidth }
    },
    { displayedTags: [], tagsContainerWidth: 0 },
  )
}

interface UseHandleKeyboardEntriesProps {
  inputRef: RefObject<HTMLDivElement>
  searchText: string
  setOpen: (open: boolean) => void
  onCreateTag: () => void
  removeSelectedValue: (valueToDelete: string | -1) => void
  setSearchText: (searchText: string) => void
}

export function useHandleKeyboardEntries(props: UseHandleKeyboardEntriesProps) {
  const {
    inputRef,
    searchText,
    onCreateTag,
    setOpen,
    removeSelectedValue,
    setSearchText,
  } = props

  function onBackspace() {
    if (searchText === "") removeSelectedValue(-1)
  }

  function onKeyDown(evt: KeyboardEvent) {
    if (evt.code === "Escape") {
      inputRef.current?.blur()
      setOpen(false)
      setSearchText("")
    }
    if (evt.key === "Backspace") onBackspace()
    if (evt.key === "Enter" && searchText !== "") onCreateTag()
  }

  return { onKeyDown }
}
